/**
 * Hook for fetching multiple event correlations in parallel
 *
 * Provides a hook that fetches correlation data for 2-3 events simultaneously
 * using TanStack Query's useQueries for parallel fetching.
 */

import { useQueries } from "@tanstack/react-query";
import { createApiClient, ApiClientError } from "@floline/api-client";
import type {
  EventCorrelationResponse,
  EventResponse,
} from "@floline/api-client/types";
import { API_URL } from "@/env";
import { correlationKeys } from "./use-event-correlation";
import type { EventWithCorrelation } from "@/components/events/multi-event-comparison-utils";

// --- Constants ---
const STALE_TIME = 5 * 60 * 1000; // 5 minutes

// --- API Client ---
const apiClient = createApiClient(API_URL);

// --- Pure Functions ---

/** Fetch single event correlation - reused from use-event-correlation */
async function fetchEventCorrelation(
  eventId: string
): Promise<EventCorrelationResponse | null> {
  try {
    const response = await apiClient.getEventCorrelation(eventId);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof ApiClientError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** Build query configuration for a single event */
export function buildCorrelationQuery(eventId: string | null) {
  return {
    queryKey: correlationKeys.detail(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Event ID is required");
      return fetchEventCorrelation(eventId);
    },
    enabled: eventId !== null,
    staleTime: STALE_TIME,
    retry: (failureCount: number, error: Error) => {
      if (
        error instanceof ApiClientError &&
        (error.status === 404 || error.status === 401)
      ) {
        return false;
      }
      return failureCount < 3;
    },
  };
}

/** Combine events with their correlation results */
export function combineEventsWithCorrelations(
  events: EventResponse[],
  correlations: (EventCorrelationResponse | null)[]
): EventWithCorrelation[] {
  return events.map((event, index) => ({
    event,
    correlation: correlations[index] ?? null,
  }));
}

/** Check if all queries are loading */
export function areAllLoading(
  queries: Array<{ isLoading: boolean }>
): boolean {
  return queries.some((q) => q.isLoading);
}

/** Check if any query has an error */
export function hasAnyError(queries: Array<{ isError: boolean }>): boolean {
  return queries.some((q) => q.isError);
}

/** Get first error from queries */
export function getFirstError(
  queries: Array<{ isError: boolean; error: Error | null }>
): Error | null {
  const failedQuery = queries.find((q) => q.isError);
  return failedQuery?.error ?? null;
}

/** Count events with sufficient correlation data */
export function countEventsWithData(
  correlations: (EventCorrelationResponse | null)[]
): number {
  return correlations.filter((c) => c && !c.insufficientData).length;
}

// --- Main Hook ---

/**
 * Hook to fetch correlation data for multiple events in parallel
 *
 * Uses useQueries to fetch correlations for all selected events simultaneously.
 * Returns combined event + correlation data for comparison display.
 *
 * @param events - Array of events to fetch correlations for (max 3)
 *
 * @example
 * ```tsx
 * function MultiEventComparison({ selectedEvents }: { selectedEvents: EventResponse[] }) {
 *   const {
 *     eventsWithCorrelations,
 *     isLoading,
 *     eventsWithSufficientData
 *   } = useMultiEventCorrelation(selectedEvents);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <ComparisonGrid events={eventsWithCorrelations} />
 *   );
 * }
 * ```
 */
export function useMultiEventCorrelation(events: EventResponse[]) {
  // Build query configurations for all events
  const queryConfigs = events.map((event) => buildCorrelationQuery(event.id));

  // Fetch all correlations in parallel
  const queries = useQueries({ queries: queryConfigs });

  // Extract correlation data from query results
  const correlations = queries.map((q) => q.data ?? null);

  // Combine events with their correlations
  const eventsWithCorrelations = combineEventsWithCorrelations(
    events,
    correlations
  );

  // Compute derived state
  const isLoading = areAllLoading(queries);
  const isError = hasAnyError(queries);
  const error = getFirstError(queries);
  const eventsWithSufficientData = countEventsWithData(correlations);

  return {
    /** Events paired with their correlation data */
    eventsWithCorrelations,
    /** Raw correlation responses */
    correlations,
    /** Whether any correlation is loading */
    isLoading,
    /** Whether any query failed */
    isError,
    /** First error encountered */
    error,
    /** Count of events with sufficient data */
    eventsWithSufficientData,
    /** Whether all events have sufficient data */
    allHaveSufficientData: eventsWithSufficientData === events.length,
    /** Refetch all correlations */
    refetchAll: () => {
      for (const q of queries) {
        q.refetch();
      }
    },
  };
}
